import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import Skeleton from '@material-ui/lab/Skeleton'
import useIsMountedRef from 'use-is-mounted-ref'
import React, { ChangeEvent, FC, MutableRefObject, ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import { ReactState } from '../../../../React/Common/Contexts'
import { SelectFilter, SelectFilterOption } from './SelectFilter'
import { CmeIcon } from '../../Atoms/Icons/Cme'
import { TelSysIcon } from '../../Atoms/Icons/TelSys'
import { PartitionIcon } from '../../Atoms/Icons/Partition'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import {
    getCMEsAsSelectOptions,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions
} from '../../../../React/Common/API/ItemsGetters'
import { SelectOption, SelectOptions } from '../../../../React/Common/Components/Selects/Select/Select'
import {
    ALL_VALUE,
    ANY_VALUE_STRING_DEFAULT,
    ANY_VALUE_STRING_NUMERIC
} from '../../../../React/Common/Constants/DefaultValues'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import TextInput from '../../../../React/Common/Components/TextInput'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import { CME } from '../../../../React/Common/Types/Types'
import { $error } from '../../../../React/Common/Utils/ConsoleUtils'
import { CRMTypes, useCRMTypes } from '../../../Hooks/useCRMTypes'
import { CrmIcon } from '../../Atoms/Icons/Crm'
import { CRMTypesResponse } from '../../../../NonReact/common/Types/Types'

export interface AdminFiltersLocaleText {
    cmeLabel: string
    telephonyServerLabel: string
    partitionLabel: string
    crmTypeLabel?: string
    includeChildCmeLabel: string
    searchLabel: string
}

export enum SelectFilterNames {
    CME = 'cme',
    TELEPHONY_SERVER = 'telephonyServer',
    PARTITION = 'partition',
    CRM_TYPE = 'crmType'
}

export enum SwitchFilterNames {
    INCLUDE_CME_CHILDREN = 'includeCmeChildren'
}

export enum TextFilterNames {
    SEARCH = 'search'
}

export type AdminFilterNames =
    | SelectFilterNames.CME
    | SelectFilterNames.CRM_TYPE
    | SelectFilterNames.PARTITION
    | SelectFilterNames.TELEPHONY_SERVER
    | SwitchFilterNames.INCLUDE_CME_CHILDREN
    | TextFilterNames.SEARCH

export interface AdminFilterValues {
    cme: Nullable<SelectFilterOption>
    telephonyServer: Nullable<SelectFilterOption>
    partition: Nullable<SelectFilterOption>
    crmType: Nullable<SelectFilterOption>
    includeCmeChildren: Nullable<boolean>
    search: Definable<string>
}

export interface AdminFilterSingleValues {
    cme?: string | number
    telephonyServer?: string | number
    partition?: string | number
    device?: string
}

type OptionsToDisplay = {
    [key in AdminFilterNames]: boolean
}

interface AdminFiltersProps {
    optionsToDisplay: OptionsToDisplay
    localeText: AdminFiltersLocaleText
    onChange: (values: AdminFilterValues) => void
    onRefresh: () => void
    cmeFiltering?: [string[], (cme: CME) => boolean]
}

export const AdminFilters: FC<AdminFiltersProps> = ({
    onChange,
    localeText,
    optionsToDisplay,
    onRefresh,
    cmeFiltering: [cmeFields, cmeFiltering] = [[], (): boolean => true]
}: AdminFiltersProps): ReactElement => {
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [cmesOpts, setCmesOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [telephonyServerOpts, setTelephonyServerOpts]: ReactState<SelectFilterOption[]> = useState<
        SelectFilterOption[]
    >([])
    const [partitionOpts, setPartitionOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [filterValues, setFilterValues]: ReactState<AdminFilterValues> = useState<AdminFilterValues>({
        cme: null,
        telephonyServer: null,
        partition: null,
        crmType: { value: ANY_VALUE_STRING_NUMERIC, label: ANY_VALUE_STRING_DEFAULT },
        includeCmeChildren: true,
        search: EMPTY_STRING
    })

    const [crmTypeOpts]: CRMTypes = useCRMTypes()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const filterDeliverySubject: MutableRefObject<Subject<AdminFilterValues>> = useRef(new Subject<AdminFilterValues>())

    const mappedCrmTypeOpts: SelectFilterOption[] = [
        { value: ANY_VALUE_STRING_NUMERIC, label: ANY_VALUE_STRING_DEFAULT },
        ...(crmTypeOpts?.map(
            ({ crmType, crmTypeName }: CRMTypesResponse): SelectFilterOption => ({
                label: crmTypeName,
                value: crmType
            })
        ) ?? [])
    ]

    const getCMEs = (): void => {
        setIsLoading(true)

        getCMEsAsSelectOptions(cmeFields, cmeFiltering)
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) {
                    setCmesOpts(
                        cmes.map(
                            ({ label, value }: SelectOption<string>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    )
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }

    const getTelServers = (cmeID: string): void => {
        setIsLoading(true)
        getTelServersAsSelectOptions(cmeID, filterValues.includeCmeChildren ?? false)
            .then((telSyss: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setTelephonyServerOpts([
                        { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
                        ...telSyss.map(
                            ({ label, value }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch((e: Error): void => {
                $error(e)

                if (isMounted.current) {
                    setTelephonyServerOpts([{ label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }])
                }
            })
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }

    const getPartitions = (telSys: number): void => {
        setIsLoading(true)
        getPartitionsForTelServer(telSys)
            .then((partitions: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setPartitionOpts([
                        { label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE },
                        ...partitions.map(
                            ({ label, value }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch((e: Error): void => {
                $error(e)

                if (isMounted.current) {
                    setPartitionOpts([{ label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }])
                }
            })
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }

    useEffect((): void => {
        setFilterValues(
            (currentFilterValues: AdminFilterValues): AdminFilterValues => ({
                ...currentFilterValues,
                cme: cmesOpts?.[0]
            })
        )
    }, [JSON.stringify(cmesOpts)])

    useEffect((): void => {
        if (isStringNotEmpty(filterValues?.cme?.value as string)) {
            setIsLoading(true)
            getTelServers(filterValues?.cme?.value as string)
        }
    }, [filterValues?.cme?.value, filterValues?.includeCmeChildren])

    useEffect((): void => {
        setFilterValues(
            (currentFilterValues: AdminFilterValues): AdminFilterValues => ({
                ...currentFilterValues,
                telephonyServer: telephonyServerOpts?.[0]
            })
        )
    }, [JSON.stringify(telephonyServerOpts)])

    useEffect((): void => {
        if (isVarNotEmpty(filterValues?.telephonyServer?.value)) {
            setIsLoading(true)

            if (filterValues.telephonyServer.value === ALL_VALUE) {
                setPartitionOpts([{ label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }])
                setIsLoading(false)
            } else {
                getPartitions(filterValues.telephonyServer.value as number)
            }
        }
    }, [filterValues?.telephonyServer?.value])

    useEffect((): void => {
        setFilterValues(
            (currentFilterValues: AdminFilterValues): AdminFilterValues => ({
                ...currentFilterValues,
                partition: partitionOpts?.[0]
            })
        )
    }, [JSON.stringify(partitionOpts)])

    useEffect(getCMEs, [])

    const handleSelectFilterChange: (filter: SelectFilterNames, option: Nullable<SelectFilterOption>) => void =
        useCallback(
            (filter: SelectFilterNames, option: Nullable<SelectFilterOption>): void => {
                const newFilterValues: AdminFilterValues = { ...filterValues, [filter]: option }

                setFilterValues(newFilterValues)
                filterDeliverySubject.current.next(newFilterValues)
            },
            [filterValues]
        )

    const handleSwitchFilterChange: (filter: SwitchFilterNames, value: Nullable<boolean>) => void = useCallback(
        (filter: SwitchFilterNames, value: Nullable<boolean>): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, [filter]: value }

            setFilterValues(newFilterValues)
            filterDeliverySubject.current.next(newFilterValues)
        },
        [filterValues]
    )

    const handleTextFilterChange: (filter: TextFilterNames, value: string) => void = useCallback(
        (filter: TextFilterNames, value: string): void => {
            const newFilterValues: AdminFilterValues = { ...filterValues, [filter]: value }

            setFilterValues(newFilterValues)
            filterDeliverySubject.current.next(newFilterValues)
        },
        [filterValues]
    )

    useEffect((): (() => void) => {
        filterDeliverySubject.current
            .pipe(debounceTime(400))
            .subscribe((value: AdminFilterValues): void => onChange(value))

        return (): void => filterDeliverySubject.current.unsubscribe()
    }, [filterDeliverySubject])

    return (
        <div className={clsx('router-admin-filters')}>
            {isLoading ? (
                <div className='router-admin-filters-loader router-admin-filters-grid'>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <div className='af-right-side'>
                        <Skeleton />
                    </div>
                </div>
            ) : (
                <div>
                    <div className='router-admin-filters-grid'>
                        {optionsToDisplay?.cme && cmesOpts?.length > 0 ? (
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        handleSelectFilterChange(SelectFilterNames.CME, value)
                                    }}
                                    options={cmesOpts}
                                    label={localeText.cmeLabel}
                                    icon={<CmeIcon />}
                                    disabled={isLoading}
                                    value={filterValues.cme}
                                    defaultValue={DEFAULT_CME_ID}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {optionsToDisplay?.telephonyServer && telephonyServerOpts?.length > 0 ? (
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        handleSelectFilterChange(SelectFilterNames.TELEPHONY_SERVER, value)
                                    }}
                                    options={telephonyServerOpts}
                                    label={localeText.telephonyServerLabel}
                                    icon={<TelSysIcon />}
                                    disabled={isLoading}
                                    value={filterValues.telephonyServer}
                                    defaultValue={ALL_VALUE}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {optionsToDisplay?.partition ? (
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        handleSelectFilterChange(SelectFilterNames.PARTITION, value)
                                    }}
                                    options={partitionOpts}
                                    label={localeText.partitionLabel}
                                    icon={<PartitionIcon />}
                                    disabled={isLoading}
                                    value={filterValues.partition}
                                    defaultValue={ALL_VALUE}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {optionsToDisplay?.crmType ? (
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        handleSelectFilterChange(SelectFilterNames.CRM_TYPE, value)
                                    }}
                                    options={mappedCrmTypeOpts}
                                    label={localeText.crmTypeLabel ?? EMPTY_STRING}
                                    icon={<CrmIcon />}
                                    disabled={isLoading}
                                    value={filterValues.crmType}
                                    defaultValue={ANY_VALUE_STRING_NUMERIC}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {optionsToDisplay?.includeCmeChildren ? (
                            <div>
                                <FormControlLabel
                                    onChange={(_: ChangeEvent<object>, value: boolean): void =>
                                        handleSwitchFilterChange(SwitchFilterNames.INCLUDE_CME_CHILDREN, value)
                                    }
                                    disabled={isLoading}
                                    control={
                                        <Switch color='primary' checked={filterValues.includeCmeChildren ?? false} />
                                    }
                                    label={localeText.includeChildCmeLabel}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        <div className='af-right-side'>
                            <IconButton
                                customClassName='refresh-button'
                                iconName={IconName.REFRESH}
                                type={IconButtonType.BUTTON}
                                onClick={onRefresh}
                            />
                            {optionsToDisplay?.search ? (
                                <TextInput
                                    className='af-text-filter'
                                    variant='outlined'
                                    labelInput={localeText.searchLabel}
                                    value={filterValues.search}
                                    adornmentEnd={
                                        filterValues.search === EMPTY_STRING ? undefined : (
                                            <IconButton
                                                type={IconButtonType.BUTTON}
                                                iconName={IconName.X}
                                                onClick={(): void =>
                                                    handleTextFilterChange(TextFilterNames.SEARCH, EMPTY_STRING)
                                                }
                                            />
                                        )
                                    }
                                    onChange={(search: string): void =>
                                        handleTextFilterChange(TextFilterNames.SEARCH, search)
                                    }
                                />
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
